import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { Fragment } from "react";
import {
  HISTORY_COLUMN_TITLES,
  HISTORY_DEFAULT_WINDOW_DAYS,
  SHIPPING_KIND_BADGE_CLASS_NAME,
  SHIPPING_KIND_LABELS,
} from "../constants";
import { useShippingDownload } from "../hooks/use-shipping-download";
import { useShippingHistory } from "../hooks/use-shipping-history";
import { DASHBOARD_LABELS } from "../messages";
import {
  DateRange,
  DeliveryStatusEnum,
  Shipping,
  ShippingRecipient,
} from "../types";
import { DeliveryBadges } from "./delivery-badges";
import { ResendButton } from "./resend-button";

const formatSentAt = (value: string | null): string => {
  if (!value) return DASHBOARD_LABELS.NOT_SENT_YET;

  const date: Date = new Date(value);
  return `${date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  })} ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const countDelivered = (shipping: Shipping): number =>
  shipping.recipients.filter(
    (recipient: ShippingRecipient) =>
      recipient.status === DeliveryStatusEnum.DELIVERED
  ).length;

interface ShippingHistoryProps {
  range: DateRange;
}

export function ShippingHistory({ range }: ShippingHistoryProps): JSX.Element {
  const {
    page,
    expandedIds,
    toggleExpanded,
    goToPreviousPage,
    goToNextPage,
  } = useShippingHistory(range);

  const { download } = useShippingDownload();

  return (
    <div className="w-full space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-[#613F4F]">
          {DASHBOARD_LABELS.HISTORY_TITLE}
        </p>
        <p className="text-xs text-muted-foreground">
          {DASHBOARD_LABELS.HISTORY_HINT}{" "}
          {DASHBOARD_LABELS.HISTORY_WINDOW(HISTORY_DEFAULT_WINDOW_DAYS)}
        </p>
      </div>

      <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:block">
        <Table>
          <TableHeader className="bg-gray-50/80">
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>{HISTORY_COLUMN_TITLES.SENT_AT}</TableHead>
              <TableHead>{HISTORY_COLUMN_TITLES.KIND}</TableHead>
              <TableHead>{HISTORY_COLUMN_TITLES.RECIPIENTS}</TableHead>
              <TableHead>{HISTORY_COLUMN_TITLES.STATUS}</TableHead>
              <TableHead>{HISTORY_COLUMN_TITLES.ACTION}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {page.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  {DASHBOARD_LABELS.HISTORY_EMPTY}
                </TableCell>
              </TableRow>
            )}

            {page.rows.map((shipping: Shipping) => {
              const kindLabel: string = SHIPPING_KIND_LABELS[shipping.kind];
              const delivered: number = countDelivered(shipping);
              const total: number = shipping.recipients.length;
              const failed: number = total - delivered;
              const expanded: boolean = expandedIds.includes(shipping.id);

              return (
                <Fragment key={shipping.id}>
                  <TableRow>
                    <TableCell>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        aria-label={`Expandir envio de ${kindLabel}`}
                        onClick={() => toggleExpanded(shipping.id)}
                      >
                        {expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-gray-700">
                      {formatSentAt(shipping.sent_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                          SHIPPING_KIND_BADGE_CLASS_NAME[shipping.kind]
                        }`}
                      >
                        {kindLabel}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {failed === 0
                        ? DASHBOARD_LABELS.RECIPIENTS_SUMMARY(delivered, total)
                        : DASHBOARD_LABELS.RECIPIENTS_SUMMARY_WITH_FAILURES(
                            delivered,
                            total,
                            failed
                          )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                          failed === 0
                            ? "border-green-200 bg-green-100 text-green-800"
                            : "border-red-200 bg-red-100 text-red-800"
                        }`}
                      >
                        {failed === 0
                          ? DASHBOARD_LABELS.SHIPPING_OK
                          : DASHBOARD_LABELS.SHIPPING_ATTENTION}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-sm text-[#3c7dd9] hover:underline"
                        onClick={() => download(shipping.id, kindLabel)}
                      >
                        <Download className="h-3.5 w-3.5" />
                        {DASHBOARD_LABELS.DOWNLOAD}
                      </button>
                    </TableCell>
                  </TableRow>

                  {expanded &&
                    shipping.recipients.map((recipient: ShippingRecipient) => (
                      <TableRow
                        key={`${shipping.id}-${recipient.id}`}
                        className="border-l-2 border-l-[#613F4F] bg-gray-50/40"
                      >
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          <p className="text-sm text-gray-900">
                            {recipient.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {recipient.email}
                          </p>
                        </TableCell>
                        <TableCell>
                          <DeliveryBadges recipient={recipient} />
                        </TableCell>
                        <TableCell>
                          <ResendButton
                            kindLabel={kindLabel}
                            recipient={recipient}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3 md:hidden">
        {page.rows.map((shipping: Shipping) => {
          const kindLabel: string = SHIPPING_KIND_LABELS[shipping.kind];

          return (
            <div
              key={shipping.id}
              className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                    SHIPPING_KIND_BADGE_CLASS_NAME[shipping.kind]
                  }`}
                >
                  {kindLabel}
                </span>
                <span className="text-xs text-gray-500">
                  {formatSentAt(shipping.sent_at)}
                </span>
              </div>

              {shipping.recipients.map((recipient: ShippingRecipient) => (
                <div
                  key={`${shipping.id}-${recipient.id}`}
                  className="space-y-1 border-t border-gray-100 pt-2"
                >
                  <p className="text-sm text-gray-900">{recipient.name}</p>
                  <p className="text-xs text-gray-500">{recipient.email}</p>
                  <DeliveryBadges recipient={recipient} />
                  <ResendButton kindLabel={kindLabel} recipient={recipient} />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3 px-2">
        <span className="text-sm text-muted-foreground">
          {page.currentPage} / {page.lastPage}
        </span>
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          aria-label={DASHBOARD_LABELS.PREVIOUS_PAGE}
          disabled={!page.hasPrevious}
          onClick={goToPreviousPage}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          aria-label={DASHBOARD_LABELS.NEXT_PAGE}
          disabled={!page.hasNext}
          onClick={goToNextPage}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
